import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import type { RouletteRewardPreview, RouletteSegmentPreview, RouletteSpinCost } from "../api/roulette";
import {
  consumableLabel,
  getConsumableIconPath,
  getGemIconPath,
  getMountIconPath,
} from "../utils/assets";

export const CLOCKWISE_SLOTS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] as const;
const ANIMATION_MS = 5000;
const MIN_LAPS = 3;

/** Grid cell placement for 4×4 board (center 2×2 is spin button). */
export const SLOT_GRID: Record<number, string> = {
  0: "col-start-1 row-start-1",
  1: "col-start-2 row-start-1",
  2: "col-start-3 row-start-1",
  3: "col-start-4 row-start-1",
  4: "col-start-4 row-start-2",
  5: "col-start-4 row-start-3",
  6: "col-start-4 row-start-4",
  7: "col-start-3 row-start-4",
  8: "col-start-2 row-start-4",
  9: "col-start-1 row-start-4",
  10: "col-start-1 row-start-3",
  11: "col-start-1 row-start-2",
};

function buildAnimationSteps(fromSlot: number, toSlot: number): number[] {
  const fromIdx = CLOCKWISE_SLOTS.indexOf(fromSlot as (typeof CLOCKWISE_SLOTS)[number]);
  const toIdx = CLOCKWISE_SLOTS.indexOf(toSlot as (typeof CLOCKWISE_SLOTS)[number]);
  const offset = (toIdx - fromIdx + CLOCKWISE_SLOTS.length) % CLOCKWISE_SLOTS.length;
  const totalSteps = MIN_LAPS * CLOCKWISE_SLOTS.length + offset;
  const steps: number[] = [];
  for (let i = 1; i <= totalSteps; i++) {
    steps.push(CLOCKWISE_SLOTS[(fromIdx + i) % CLOCKWISE_SLOTS.length]);
  }
  return steps;
}

function stepDelays(totalSteps: number, totalMs: number): number[] {
  const weights = Array.from({ length: totalSteps }, (_, i) => (i + 1) ** 1.75);
  const sum = weights.reduce((a, b) => a + b, 0);
  return weights.map((w) => (w / sum) * totalMs);
}

function RewardIcon({ reward }: { reward: RouletteRewardPreview }) {
  if (reward.type === "gold") {
    return <span className="text-2xl leading-none">🪙</span>;
  }
  if (reward.type === "diamonds") {
    return <span className="text-2xl leading-none">💎</span>;
  }
  if (reward.type === "mount_stones") {
    return <span className="text-2xl leading-none">🪨</span>;
  }
  if (reward.type === "mount_card" && reward.mount_code) {
    return (
      <img
        src={getMountIconPath(reward.mount_code)}
        alt={reward.label}
        className="w-8 h-8 object-cover rounded"
      />
    );
  }
  if (reward.type === "gem" && reward.gem_type && reward.level) {
    return (
      <img
        src={getGemIconPath(reward.gem_type, reward.level)}
        alt={reward.label}
        className="w-8 h-8 object-contain"
      />
    );
  }
  return (
    <img
      src={getConsumableIconPath(reward.item_id ?? reward.icon_hint)}
      alt={reward.label}
      className="w-8 h-8 object-contain"
    />
  );
}

function SpinCostIcon({ cost }: { cost: RouletteSpinCost }) {
  if (cost.type === "gold") return <span>🪙</span>;
  if (cost.type === "diamonds") return <span>💎</span>;
  return (
    <img
      src={getConsumableIconPath(cost.item_id ?? "roulette_ticket")}
      alt=""
      className="w-5 h-5 object-contain inline-block"
    />
  );
}

interface RouletteBoardProps {
  segments: RouletteSegmentPreview[];
  spinCost: RouletteSpinCost;
  costAvailable: number;
  spinning: boolean;
  skipAnimation: boolean;
  pendingWinSlot: number | null;
  onSpin: () => Promise<number>;
  onAnimationComplete: (slot: number) => void;
  spinLabel?: string;
  hideCostHint?: boolean;
}

export function RouletteBoard({
  segments,
  spinCost,
  costAvailable,
  spinning,
  skipAnimation,
  pendingWinSlot,
  onSpin,
  onAnimationComplete,
  spinLabel = "КРУТИТЬ",
  hideCostHint = false,
}: RouletteBoardProps) {
  const [highlightedSlot, setHighlightedSlot] = useState<number | null>(null);
  const timersRef = useRef<number[]>([]);
  const lastSlotRef = useRef<number>(0);

  const segmentBySlot = new Map(segments.map((s) => [s.slot, s]));
  const canAfford = costAvailable >= spinCost.amount;

  useEffect(() => {
    return () => {
      timersRef.current.forEach((t) => window.clearTimeout(t));
      timersRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (pendingWinSlot === null) return;

    timersRef.current.forEach((t) => window.clearTimeout(t));
    timersRef.current = [];

    const fromSlot = lastSlotRef.current;
    const toSlot = pendingWinSlot;

    if (skipAnimation) {
      setHighlightedSlot(toSlot);
      lastSlotRef.current = toSlot;
      onAnimationComplete(toSlot);
      return;
    }

    const steps = buildAnimationSteps(fromSlot, toSlot);
    const delays = stepDelays(steps.length, ANIMATION_MS);
    let elapsed = 0;

    steps.forEach((slot, idx) => {
      elapsed += delays[idx];
      const timer = window.setTimeout(() => {
        setHighlightedSlot(slot);
        if (idx === steps.length - 1) {
          lastSlotRef.current = slot;
          onAnimationComplete(slot);
        }
      }, elapsed);
      timersRef.current.push(timer);
    });
  }, [pendingWinSlot, skipAnimation, onAnimationComplete]);

  const handleSpinClick = async () => {
    if (spinning || !canAfford) return;
    timersRef.current.forEach((t) => window.clearTimeout(t));
    timersRef.current = [];
    await onSpin();
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 grid-rows-4 gap-2 aspect-square max-w-sm mx-auto">
        {CLOCKWISE_SLOTS.map((slot) => {
          const seg = segmentBySlot.get(slot);
          const active = highlightedSlot === slot;
          return (
            <div
              key={slot}
              className={clsx(
                SLOT_GRID[slot],
                "rounded-xl border-2 bg-kingdom-dark/80 flex flex-col items-center justify-center p-1 transition-all duration-150",
                active
                  ? "border-gold-400 shadow-[0_0_16px_rgba(251,191,36,0.55)] scale-[1.03] z-10"
                  : "border-kingdom-mid",
              )}
            >
              {seg ? (
                <>
                  <RewardIcon reward={seg.reward_preview} />
                  <span className="text-[9px] text-gray-300 text-center leading-tight mt-0.5 line-clamp-2">
                    {seg.reward_preview.label}
                  </span>
                </>
              ) : (
                <span className="text-gray-600 text-xs">—</span>
              )}
            </div>
          );
        })}

        <button
          type="button"
          onClick={() => void handleSpinClick()}
          disabled={spinning || !canAfford}
          className={clsx(
            "col-start-2 col-span-2 row-start-2 row-span-2 rounded-2xl font-game font-bold text-lg",
            "bg-gradient-to-br from-gold-500 to-amber-700 text-black shadow-lg",
            "flex flex-col items-center justify-center gap-1 active:scale-95 transition-transform",
            "disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100",
          )}
        >
          <span>{spinning ? "…" : spinLabel}</span>
          {!hideCostHint && (
            <span className="text-sm font-semibold flex items-center gap-1">
              <SpinCostIcon cost={spinCost} />
              <span>×{spinCost.amount}</span>
            </span>
          )}
        </button>
      </div>

      {!hideCostHint && (
        <p className="text-center text-xs text-gray-400">
        {spinCost.type === "consumable" ? (
          <>
            {consumableLabel(spinCost.item_id ?? "roulette_ticket")}: {costAvailable} available
          </>
        ) : (
          <>
            Cost: {spinCost.amount} {spinCost.type} · you have {costAvailable}
          </>
        )}
      </p>
      )}
    </div>
  );
}

export { ANIMATION_MS };
